import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function AboutPage() {
  useSEO({
    title: 'About Mr. EcoClean - Sacramento Pressure Washing Experts Since 2016',
    description: 'Learn about Mr. EcoClean, Sacramento\'s trusted pressure washing company since 2016. Licensed, insured, eco-friendly services. Meet our experienced team dedicated to quality and customer satisfaction.',
    keywords: 'about Mr. EcoClean, pressure washing company Sacramento, licensed pressure washing, insured pressure washing, eco-friendly cleaning Sacramento',
    canonical: '/about',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Mr. EcoClean",
      "description": "Learn about Mr. EcoClean, Sacramento's trusted pressure washing company",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Mr. EcoClean",
        "foundingDate": "2016",
        "description": "Professional pressure washing services for residential and commercial properties in Sacramento CA",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://mrecoclean.com'}`,
        "telephone": "+19168366145",
        "email": "Jason@mrecoclean.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Sacramento",
          "addressRegion": "CA",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 38.5816,
          "longitude": -121.4944
        },
        "areaServed": [
          {
            "@type": "City",
            "name": "Sacramento"
          },
          {
            "@type": "City",
            "name": "Elk Grove"
          },
          {
            "@type": "City",
            "name": "Roseville"
          }
        ],
        "slogan": "Professional, Eco-Friendly Pressure Washing Services",
        "knowsAbout": ["Pressure Washing", "Solar Panel Cleaning", "House Washing", "Commercial Cleaning", "Eco-Friendly Cleaning"],
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5",
          "reviewCount": "127"
        }
      }
    }
  });

  const teamMembers = [
    {
      name: "Mike Rodriguez",
      role: "Owner & Lead Technician",
      experience: "8+ years",
      image: "https://public.readdy.ai/ai/img_res/79bbcaac980aa5abf9ffd02da18bdd21.jpg"
    },
    {
      name: "Sarah Chen",
      role: "Operations Manager",
      experience: "5+ years",
      image: "https://readdy.ai/api/search-image?query=Professional%20business%20operations%20manager%20woman%20in%20office%20setting%2C%20friendly%20Asian%20woman%20with%20professional%20attire%2C%20Sacramento%20business%20professional%2C%20clean%20modern%20office%20environment%20with%20pressure%20washing%20business%20materials&width=300&height=300&seq=team-sarah&orientation=squarish"
    },
    {
      name: "David Thompson",
      role: "Senior Technician",
      experience: "6+ years",
      image: "https://readdy.ai/api/search-image?query=Experienced%20pressure%20washing%20technician%20in%20uniform%20with%20professional%20equipment%2C%20friendly%20African%20American%20man%20with%20safety%20gear%2C%20Sacramento%20service%20professional%2C%20clean%20pressure%20washing%20truck%20and%20equipment%20background&width=300&height=300&seq=team-david&orientation=squarish"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="cursor-pointer">
                <h1 className="text-2xl font-bold text-red-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  Mr. EcoClean
                </h1>
              </Link>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/services" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Services</Link>
              <Link to="/areas-covered" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Areas Covered</Link>
              <Link to="/about" className="text-red-600 font-medium whitespace-nowrap cursor-pointer">About</Link>
              <Link to="/contact" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button className="text-gray-700 hover:text-red-600 cursor-pointer">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-red-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">About Mr. EcoClean</h2>
          <p className="text-xl text-red-100 max-w-3xl mx-auto">
            Your trusted local pressure washing professionals serving Sacramento and surrounding communities since 2016.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h3>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Mr. EcoClean was founded in 2016 with a simple mission: to provide Sacramento homeowners and businesses with professional, eco-friendly pressure washing services that restore and protect their properties.
                </p>
                <p>
                  What started as a small family business has grown into one of Sacramento's most trusted pressure washing companies. We've built our reputation on quality work, reliable service, and a commitment to using environmentally safe cleaning methods.
                </p>
                <p>
                  Today, we're proud to serve hundreds of satisfied customers throughout Sacramento, Elk Grove, Roseville, and surrounding communities. Our team of experienced professionals uses state-of-the-art equipment and eco-friendly cleaning solutions to deliver exceptional results every time.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/1fe53895dbc344f81e4ae10fc5461665.jpeg"
                alt="Mr. EcoClean team at work in Sacramento"
                className="rounded-xl shadow-lg w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Mission & Values</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're committed to excellence in everything we do, from customer service to environmental responsibility.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-leaf-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Eco-Friendly</h4>
              <p className="text-gray-600">
                We use only environmentally safe cleaning products and water-efficient techniques to protect our planet while delivering outstanding results.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Quality Excellence</h4>
              <p className="text-gray-600">
                We take pride in our work and guarantee 100% satisfaction on every job. Our attention to detail sets us apart from the competition.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-line text-2xl text-sky-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Community Focus</h4>
              <p className="text-gray-600">
                As a local Sacramento business, we're invested in our community. We support local charities and treat every customer like family.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h3>
            <p className="text-xl text-gray-600">
              Experienced professionals dedicated to delivering exceptional pressure washing services.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 text-center">
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover object-top"
                />
                <h4 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h4>
                <p className="text-sky-600 font-semibold mb-2">{member.role}</p>
                <p className="text-gray-600">{member.experience} experience</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Insurance */}
      <section className="py-20 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Licensed, Insured & Certified</h3>
            <p className="text-xl text-gray-600">
              Your peace of mind is our priority. We maintain all necessary licenses and insurance coverage.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="font-semibold text-gray-900 mb-2">Fully Licensed</h5>
              <p className="text-sm text-gray-600">California contractor's license</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-umbrella-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="font-semibold text-gray-900 mb-2">Fully Insured</h5>
              <p className="text-sm text-gray-600">$2M liability coverage</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="font-semibold text-gray-900 mb-2">Certified Technicians</h5>
              <p className="text-sm text-gray-600">Professional training & certification</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 text-center shadow-md">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-line text-2xl text-sky-600"></i>
              </div>
              <h5 className="font-semibold text-gray-900 mb-2">5-Star Rated</h5>
              <p className="text-sm text-gray-600">Excellent customer reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h3>
            <p className="text-xl text-gray-600">
              Don't just take our word for it - hear from our satisfied customers.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-600 mb-6">
                "Mr. EcoClean did an amazing job on our house and driveway. The team was professional, punctual, and the results exceeded our expectations. Highly recommended!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mr-4">
                  <span className="font-semibold text-sky-600">JM</span>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900">Jennifer Martinez</h5>
                  <p className="text-sm text-gray-600">Sacramento Homeowner</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-600 mb-6">
                "We've used Mr. EcoClean for our commercial property for three years. They're reliable, thorough, and always deliver excellent results. Great value for the price."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mr-4">
                  <span className="font-semibold text-sky-600">RJ</span>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900">Robert Johnson</h5>
                  <p className="text-sm text-gray-600">Business Owner</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-600 mb-6">
                "The eco-friendly approach was important to us, and Mr. EcoClean delivered. Our deck looks brand new, and we feel good about the environmental impact."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center mr-4">
                  <span className="font-semibold text-sky-600">LW</span>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900">Lisa Wang</h5>
                  <p className="text-sm text-gray-600">Elk Grove Resident</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">Ready to Experience the Difference?</h3>
          <p className="text-xl text-red-100 mb-8">
            Join hundreds of satisfied customers who trust Mr. EcoClean for their pressure washing needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
            <a 
              href="tel:916-836-6145"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call: (916) 836-6145
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold text-sky-400 mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                Mr. EcoClean
              </h4>
              <p className="text-gray-300 mb-4">
                Professional pressure washing services for Sacramento and surrounding areas.
              </p>
              <div className="flex space-x-4">
                <i className="ri-facebook-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-instagram-line text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-google-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">House Washing</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Driveway Cleaning</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Commercial Cleaning</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Contact Info</h5>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  <a href="tel:916-836-6145" className="hover:text-sky-400 cursor-pointer">(916) 836-6145</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:Jason@mrecoclean.com" className="hover:text-sky-400 cursor-pointer">Jason@mrecoclean.com</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Sacramento, CA
                </p>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Business Hours</h5>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                <p>Saturday: 8:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Mr. EcoClean. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-sky-400 cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
